"use client"

import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAdminAuthStore } from '@/domains/admin/stores'
import { usePermissions } from '@/shared/hooks/use-permissions'
import type { UserPermission } from '@shared/constants/user-constants'

interface SecureRouteProps {
  children: ReactNode
  requiredPermissions?: UserPermission[]
  requireAll?: boolean
}

export function SecureRoute({ 
  children, 
  requiredPermissions = [], 
  requireAll = true 
}: SecureRouteProps) {
  const navigate = useNavigate()
  const { isAuthenticated, refreshUser, error } = useAdminAuthStore()
  const { isPermissionsLoaded, hasAllPermissions, hasAnyPermission } = usePermissions()
  
  const [hasAttemptedRefresh, setHasAttemptedRefresh] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(false)


  const permissionState = useMemo(() => {
    if (!isPermissionsLoaded || requiredPermissions.length === 0) {
      return { hasAccess: true, isChecking: false }
    }
    
    const hasAccess = requireAll 
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions)
    
    return { hasAccess, isChecking: false }
  }, [isPermissionsLoaded, requiredPermissions, requireAll, hasAllPermissions, hasAnyPermission])

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated && !hasAttemptedRefresh && !isCheckingAuth) {
        setIsCheckingAuth(true)
        setHasAttemptedRefresh(true)
        try {
          await refreshUser()
          const currentState = useAdminAuthStore.getState()
          if (!currentState.isAuthenticated) {
            await navigate({ to: "/auth/login" })
          }
        } catch (error) {
          await navigate({ to: "/auth/login" })
        } finally {
          setIsCheckingAuth(false)
        }
      } else if (!isAuthenticated && hasAttemptedRefresh && !isCheckingAuth) {
        await navigate({ to: "/auth/login" })
      }
    }
    
    if (!isAuthenticated && !hasAttemptedRefresh && !isCheckingAuth) {
      checkAuth().catch(console.error)
    }
  }, [isAuthenticated, refreshUser, navigate, hasAttemptedRefresh, isCheckingAuth])

  useEffect(() => {
    if (!isAuthenticated && hasAttemptedRefresh && !isCheckingAuth) {
      navigate({ to: "/auth/login" }).catch(console.error)
    }
  }, [isAuthenticated, hasAttemptedRefresh, isCheckingAuth, navigate])

  useEffect(() => {
    if (error?.code === 'SESSION_EXPIRED') {
      navigate({ to: "/auth/login" }).catch(console.error)
    }
  }, [error, navigate])

  useEffect(() => {
    if (isPermissionsLoaded && requiredPermissions.length > 0 && !permissionState.hasAccess) {
      navigate({ to: '/forbidden', replace: true }).catch(() => {})
    }
  }, [isPermissionsLoaded, requiredPermissions.length, permissionState.hasAccess, navigate])

  useEffect(() => {
    if (isAuthenticated) {
      setHasAttemptedRefresh(false)
      setIsCheckingAuth(false)
    }
  }, [isAuthenticated])

  if (!isAuthenticated && hasAttemptedRefresh && !isCheckingAuth) {
    return null
  }

  if (requiredPermissions.length > 0 && isPermissionsLoaded && !permissionState.hasAccess) {
    return null
  }

  return <>{children}</>
}
